import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, BarChart3, Check, Clock, Filter, Loader2, Mail, MessageSquare, Pause, Play, Plus, Repeat, Siren, Sparkles, Users, Workflow } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';
type PrimitiveId = 'chats' | 'shared' | 'workflows' | 'pulses' | 'skills';
const PRIMITIVES: Array<{
  id: PrimitiveId;
  label: string;
  icon: React.ElementType;
  copy: string;
}> = [{
  id: 'chats',
  label: 'AI chats',
  icon: MessageSquare,
  copy: 'A familiar chat interface, except it is signed into your tools. Enough to replace most standalone ChatGPT use.'
}, {
  id: 'shared',
  label: 'Team workspaces',
  icon: Users,
  copy: 'Tracks and groups keep work, files, and access together, so a project has one place instead of living in personal accounts.'
}, {
  id: 'workflows',
  label: 'Workflows',
  icon: Workflow,
  copy: 'Turn a process you repeat into defined steps with declared inputs, then run it on demand and see every run.'
}, {
  id: 'pulses',
  label: 'Pulses',
  icon: Repeat,
  copy: 'Work that runs without being asked — on a schedule, reporting into the channel that needs it.'
}, {
  id: 'skills',
  label: 'Skills',
  icon: Sparkles,
  copy: 'Show Giga how your company does something once, and it reuses that method everywhere else.'
}];

/**
 * One row treatment for every list in these screens. A faint warm fill plus a
 * 1px contact shadow gives each row a surface, which is the difference between
 * a product screenshot and an outlined box.
 */
const ROW = 'rounded-xl border border-[#EFEAE1] bg-[#FCFAF6] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_1px_2px_-1px_rgba(43,33,26,0.06)]';
export function PlatformPillar() {
  const [active, setActive] = useState<PrimitiveId>('chats');
  return <div data-id="element-445" data-mp="e27u6915xt5r">
      <SectionIntro className="mx-auto max-w-3xl text-center" data-id="element-446" data-mp="e3ss88moru8d">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-447" data-mp="946c9rfh3zvv">
          Workspace
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-448" data-mp="yt3fldkyw9pd">
          One place to manage
          <br data-id="element-449" data-mp="17rfe01s2sbsd" />
          AI within your company
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-450" data-mp="17u1lk1eyural">
          Let your power users share their skills with other team members. Build automations in plain English. Schedule them with Pulses. Everything managed in one place, that you own, in the cloud.
        </p>
      </SectionIntro>

      <div className="mt-12 grid items-start gap-8 md:mt-16 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)] lg:gap-12" data-id="element-451" data-mp="1qigc6nldg3av">
        {/* Primitives as a list you step through: only the active one carries
         its explanation, so five ideas never shout at once. */}
        <ul className="space-y-1.5" data-id="element-452" data-mp="20i754kuaaa8u">
          {PRIMITIVES.map(p => {
          const on = active === p.id;
          return <li key={p.id} data-id="element-453" data-mp="25d2x5kzisxly">
                {/* Selection is carried by an orange rail and a warm fill
                 rather than a floating white card — the card read as a second
                 panel competing with the app frame beside it. */}
                <button type="button" onClick={() => setActive(p.id)} aria-current={on ? 'true' : undefined} className={`relative w-full rounded-xl py-4 pl-5 pr-4 text-left transition-[background-color,color] duration-200 ease-out ${on ? 'bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.08)] ring-1 ring-[#EFEAE1]' : 'hover:bg-[#F6F2EA]'}`} data-id="element-454" data-mp="3g0d52fnhkrgm">
                  <span className={`absolute bottom-3 left-0 top-3 w-[3px] rounded-full transition-colors duration-200 ease-out ${on ? 'bg-[#EA580C]' : 'bg-transparent'}`} aria-hidden="true" data-id="element-455" data-mp="7lusc0mlonqo" />
                  <span className="flex items-center gap-2.5" data-id="element-456" data-mp="3tx0ehhwmla3f">
                    <p.icon size={17} strokeWidth={1.9} className={on ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-457" data-mp="v9uudwpd40kk" />
                    <span className={`font-title text-xl font-bold tracking-tight ${on ? 'text-[#2B211A]' : 'text-[#6B5D52]'}`} data-id="element-458" data-mp="qgtdjj5lhqyc">
                      {p.label}
                    </span>
                  </span>
                  {on && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="mt-2.5 pl-[27px] text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-459" data-mp="fxd2rnes8cqd">
                      {p.copy}
                    </motion.p>}
                </button>
              </li>;
        })}
        </ul>

        {/* No app nav on any of the five. The numbered rail to the left is
         already the navigation for this section, so a sidebar inside the frame
         put two columns of it around one screen — and each screen states what
         it is in its own heading anyway. */}
        <AppChrome sidebar={false} data-id="element-460" data-mp="1hh72t7gs4uil">
          <motion.div key={active} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className={active === 'chats' ? 'flex h-full flex-col' : 'px-6 py-6 md:px-9 md:py-8'} data-id="element-461" data-mp="1hi9rr9ujkmvv">
            {active === 'chats' && <ChatsScreen data-id="element-462" data-mp="36ylr9fm2vy5c" />}
            {active === 'shared' && <SharedScreen data-id="element-463" data-mp="36zwwljv69dyk" />}
            {active === 'workflows' && <WorkflowsScreen data-id="element-464" data-mp="2j8rfl1dhwfmo" />}
            {active === 'pulses' && <PulsesScreen data-id="element-465" data-mp="2e8vf3c40b6bv" />}
            {active === 'skills' && <SkillsScreen data-id="element-466" data-mp="2eag64rr4474z" />}
          </motion.div>
        </AppChrome>
      </div>
    </div>;
}
function ScreenHead({
  title,
  meta
}: {
  title: string;
  meta: string;
}) {
  // A real app view states what you're looking at and then rules off. The
  // hairline is what stops these reading as floating wireframe blocks.
  return <div className="mb-6 border-b border-[#F1ECE3] pb-4" data-id="element-467" data-mp="7ka0zbyvc4qp">
      <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-468" data-mp="3g28r4t7c6nq">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] tabular-nums text-neutral-500" data-id="element-469" data-mp="2wdez63wr7ock">
        {meta}
      </p>
    </div>;
}

// The answer, as the thing a revenue lead would actually want on screen: which
// accounts, what they're worth, and the one fact that makes each a risk. Prose
// alone made the reader reassemble a table in their head.
const AT_RISK = [{
  account: 'Northwind Labs',
  value: '$84k',
  closes: '12 Sep',
  reason: 'No exec contact in 41 days · 2 tickets open'
}, {
  account: 'Meridian Health',
  value: '$61k',
  closes: '28 Sep',
  reason: 'Champion left in July · no replacement named'
}, {
  account: 'Fairline Group',
  value: '$37k',
  closes: '30 Sep',
  reason: 'Usage down 38% month over month'
}];
export function ChatsScreen() {
  return <div className="flex h-full min-h-[420px] flex-col" data-id="element-470" data-mp="1uta4loyuv5vr">
      {/* Thread header — what this chat is and which group owns it. */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-[#F1ECE3] px-5 py-3.5 md:px-7" data-id="element-471" data-mp="1yu3takk4gopp">
        <div className="min-w-0" data-id="element-472" data-mp="1ra1fhx04qclo">
          <h3 className="truncate font-title text-[16px] font-semibold tracking-[-0.01em] text-[#2B211A]" data-id="element-473" data-mp="1md2rlw7271vx">
            Renewal risk in Salesforce
          </h3>
          <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-474" data-mp="1idk6azbbsosk">
            <span className="flex size-4 items-center justify-center rounded bg-[#134E4A] text-[7px] font-bold text-white" data-id="element-475" data-mp="1deqtl93v51tg">
              SL
            </span>
            Sales
            <span className="text-[#D8CFC2]" data-id="element-476" data-mp="13l9ze3ztdkp9">·</span>
            Today, 08:42
          </p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1 text-[11px] font-medium text-neutral-500 sm:inline-flex" data-id="element-477" data-mp="ynzzfuestqn6">
          <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-478" data-mp="plqpp6rcu1au" />
          8 tools connected
        </span>
      </div>

      {/* Transcript */}
      <div className="flex-1 space-y-5 overflow-hidden px-5 py-5 md:px-7" data-id="element-479" data-mp="kqbg4r0dqrk8">
        <div className="flex justify-end" data-id="element-480" data-mp="28lg6sz8glw5v">
          <p className="max-w-[80%] rounded-2xl rounded-br-md bg-[#F4F0E9] px-4 py-2.5 text-[13.5px] font-medium text-[#2B211A]" data-id="element-481" data-mp="2dk9nt1myg5as">
            Which renewals due this quarter look at risk?
          </p>
        </div>

        <div className="flex gap-3" data-id="element-482" data-mp="24ia2vy8tkh2k">
          <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[#2B211A]" data-id="element-483" data-mp="24h8kf8ph4sat">
            <Sparkles size={13} strokeWidth={2} className="text-white" data-id="element-484" data-mp="1ps2nxsi0tg6m" />
          </span>
          <div className="min-w-0 flex-1 space-y-3.5" data-id="element-485" data-mp="1um6w2f30vu9a">
            {/* What it read, before what it concluded. */}
            <div className="relative space-y-1.5 pl-3.5 before:absolute before:bottom-1.5 before:left-0 before:top-1.5 before:w-[2px] before:rounded-full before:bg-[#EFEAE1] before:content-['']" data-id="element-486" data-mp="1grnyqi1xrgxu">
              {[['salesforce', 'Read 34 opportunities closing this quarter'], ['intercom', 'Checked support volume per account'], ['gmail', 'Scanned 60 days of thread activity']].map(([slug, label]) => <p key={label} className="flex items-center gap-2 text-[12px] text-neutral-500" data-id="element-487" data-mp="saov619dqh0w">
                  <span className="flex size-[18px] shrink-0 items-center justify-center rounded-md border border-[#EFEAE1] bg-white" data-id="element-488" data-mp="nbm1fcyunx88">
                    <BrandLogo slug={slug} className="size-[11px]" data-id="element-489" data-mp="x7yse330fwza" />
                  </span>
                  {label}
                  <Check size={11} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-490" data-mp="1b6oq9kxnm0op" />
                </p>)}
            </div>

            <p className="text-[13.5px] leading-relaxed text-[#3D3229]" data-id="element-491" data-mp="167u7uk3lme54">
              <strong className="font-semibold text-[#2B211A]" data-id="element-492" data-mp="1kw89wst6xykm">
                Three of eleven look at risk
              </strong>{' '}
              — $182k of $604k closing this quarter.
            </p>

            <ul className="divide-y divide-[#F1ECE3] overflow-hidden rounded-xl border border-[#EFEAE1] bg-[#FCFAF6]" data-id="element-493" data-mp="1tyzm4utw4ydb">
              {AT_RISK.map(r => <li key={r.account} className="flex items-start gap-3 px-3.5 py-2.5" data-id="element-494" data-mp="1pu9vbke3a9z9">
                  <span className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-400" data-id="element-495" data-mp="3f8pw7hyp0w05" />
                  <span className="min-w-0 flex-1" data-id="element-496" data-mp="3af3tahic06hj">
                    <span className="block truncate text-[13px] font-semibold text-[#2B211A]" data-id="element-497" data-mp="35hbxvdiha28l">
                      {r.account}
                    </span>
                    <span className="block truncate text-[11.5px] text-neutral-500" data-id="element-498" data-mp="30kbtd71a17jl">
                      {r.reason}
                    </span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-499" data-mp="3t87ow8f40tes">
                    <span className="block text-[13px] font-semibold tabular-nums text-[#2B211A]" data-id="element-500" data-mp="3o833q539bai8">
                      {r.value}
                    </span>
                    <span className="block text-[11px] tabular-nums text-neutral-400" data-id="element-501" data-mp="3k5ycte470rza">
                      {r.closes}
                    </span>
                  </span>
                </li>)}
            </ul>

            <p className="text-[13px] text-neutral-500" data-id="element-502" data-mp="28q1c6smzsjm8">
              I've drafted outreach for each owner — want me to send it?
            </p>
            {/* Offering the next move as buttons is what makes this a working
             surface rather than a transcript of one. */}
            <div className="flex flex-wrap gap-2" data-id="element-503" data-mp="x96w0mpzfteq">
              <span className="rounded-lg bg-[#2B211A] px-3 py-1.5 text-[12px] font-semibold text-white" data-id="element-504" data-mp="120f3vebnz6ps">
                Send all three
              </span>
              <span className="rounded-lg border border-[#E7E0D4] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#2B211A]" data-id="element-505" data-mp="16za0bj2k0tsd">
                Review drafts
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 px-5 pb-5 md:px-7" data-id="element-506" data-mp="1kzt2wao937n5">
        <div className="flex items-center gap-2.5 rounded-2xl border border-[#E7E0D4] bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(43,33,26,0.05)]" data-id="element-507" data-mp="1kxglpzqbh1b5">
          <Plus size={16} strokeWidth={1.9} className="shrink-0 text-neutral-400" data-id="element-508" data-mp="3mq4jvuo3syk2" />
          <span className="min-w-0 flex-1 truncate text-[13px] text-neutral-400" data-id="element-509" data-mp="tvxk4f8twqt2">
            Ask anything across your connected tools
          </span>
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A]" data-id="element-510" data-mp="oz741rx0xv7n">
            <ArrowUp size={13} strokeWidth={2.6} className="text-white" data-id="element-511" data-mp="12v14dmjqfc95" />
          </span>
        </div>
      </div>
    </div>;
}
export function SharedScreen() {
  // Named after the departments any company would recognise. The previous set
  // ("Giga Master", "NoContact Ops") was Giga's own internal workspace, so a
  // visitor had to decode what each one was before the permissions idea landed.
  const groups = [{
    name: 'Leadership',
    initials: 'LD',
    color: '#1E3A5F',
    members: 5,
    chats: 96,
    tracks: 14
  }, {
    name: 'Sales',
    initials: 'SL',
    color: '#134E4A',
    members: 7,
    chats: 214,
    tracks: 33
  }, {
    name: 'Marketing',
    initials: 'MK',
    color: '#7C2D12',
    members: 6,
    chats: 88,
    tracks: 12
  }, {
    name: 'Customer Success',
    initials: 'CS',
    color: '#3F3A2E',
    members: 4,
    chats: 131,
    tracks: 21
  }];
  return <div data-id="element-512" data-mp="2f5icrfmemf2y">
      <ScreenHead title="Groups" meta="Access follows the group, not the item" data-id="element-513" data-mp="2f58mrs1d07x9" />
      <ul className="space-y-2.5" data-id="element-514" data-mp="20a2fl1qj232c">
        {groups.map(g => <li key={g.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-515" data-mp="258vpylaipdya">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-xl text-[10.5px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" style={{
          backgroundColor: g.color
        }} data-id="element-516" data-mp="1ria54r1i6txr">
              {g.initials}
            </span>
            <span className="min-w-0 flex-1" data-id="element-517" data-mp="1mie512vxfmnh">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-518" data-mp="1mj72fd8zwzsy">
                {g.name}
              </span>
              <span className="font-mono text-[11px] text-neutral-400" data-id="element-519" data-mp="1ckhuac6witpw">
                spaces/{g.name.toLowerCase().replace(/\s+/g, '-')}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-520" data-mp="15jmfk1kudfyi">
              {[[g.members, 'members'], [g.chats, 'chats'], [g.tracks, 'tracks']].map(([n, l]) => <span key={String(l)} title={String(l)} className="min-w-[30px] rounded-lg border border-[#EFEAE1] bg-white px-2 py-1 text-center text-[11px] font-semibold tabular-nums text-neutral-500" data-id="element-521" data-mp="vqop999lympc">
                  {n}
                </span>)}
            </span>
          </li>)}
      </ul>
    </div>;
}
const OWNER_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/b394cd62-6cbf-40a2-98a1-4af4a23e89dd.jpg';

// Every step names the tools it touches — a step with no marks left a visible
// gap in the column and read as a missing image rather than a deliberate blank.
const STEP_TOOLS: string[][] = [['jira', 'linear'], ['intercom', 'salesforce'], ['notion'], ['slack', 'gmail']];
export function WorkflowsScreen() {
  const steps = ['Pull what shipped, slipped and stalled from Jira and Linear', 'Check every account flagged in support or gone quiet', 'Draft the update into the team space — wins, risks, decisions', 'Post to #team-update and email the leadership list'];
  return <div data-id="element-522" data-mp="2jguzp37n7typ">
      {/* Name, then one line of what it produces, then the health of it as
       tags on the right. You should not have to read the steps to know why
       this workflow exists. */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-6 gap-y-4 border-b border-[#F1ECE3] pb-5" data-id="element-523" data-mp="1rqqh9a3g1h04">
        <div className="min-w-0" data-id="element-524" data-mp="1vsbp2h14f53g">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-525" data-mp="1vtdbtiri1g10">
            Monday morning update
          </h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-[#6B5D52]" data-id="element-526" data-mp="25qj1qnodn0zn">
            Every Monday, 08:00
            <span className="text-[#D8CFC2]" data-id="element-527" data-mp="3c5k2d6ock7ck">·</span>
            <BrandLogo slug="slack" className="size-3.5" data-id="element-528" data-mp="3h4dcqq8c7i8i" />
            <span className="font-medium text-[#2B211A]" data-id="element-529" data-mp="3h6zjinneltpo">#team-update</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2" data-id="element-530" data-mp="2rikf65vyic7s">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/20 bg-emerald-50 px-2.5 py-1.5 text-[11.5px] font-semibold text-emerald-700" data-id="element-531" data-mp="2mh4tqcpva3ov">
            <span className="size-1.5 rounded-full bg-emerald-500" data-id="element-532" data-mp="2wgvp0gm94mq0" />
            Active
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-533" data-mp="28rb4htag9hem">
            <Repeat size={11} className="text-neutral-400" data-id="element-534" data-mp="2igku92htmnzk" />
            26 runs
          </span>
          {/* The bar earns its place: 96% means more with 25 of 26 ticks
           filled beside it than as a number on its own. */}
          <span className="inline-flex items-center gap-2 rounded-full border border-[#EFEAE1] bg-white px-2.5 py-1.5 text-[11.5px] font-semibold tabular-nums text-neutral-600" data-id="element-535" data-mp="1py78405ukqgb">
            <span className="flex items-end gap-[2px]" data-id="element-536" data-mp="hlfvz1vstxg">
              {Array.from({
              length: 8
            }, (_, i) => <span key={i} className={`h-2.5 w-[2.5px] rounded-full ${i === 5 ? 'bg-red-400' : 'bg-emerald-500/80'}`} data-id="element-537" data-mp="5axsfatada9u" />)}
            </span>
            96%
          </span>
        </div>
      </div>

      {/* Who owns it and who can see it — the questions the steps can't
       answer. The read/write strip that sat here only restated them. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-538" data-mp="o87fd1xtl5vu">
        <span className="flex items-center gap-2.5" data-id="element-539" data-mp="o64necd3x2oh">
          <img src={OWNER_AVATAR} alt="" aria-hidden="true" className="size-7 shrink-0 rounded-full object-cover shadow-[0_0_0_1px_rgba(43,33,26,0.08)]" data-id="element-540" data-mp="a97qpuab4qzo" />
          <span data-id="element-541" data-mp="ebw0c46ui1xp">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-542" data-mp="2uyx03rs8rkkz">
              Owner
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-543" data-mp="2z11w2x65wtas">
              Sarah Chen
            </span>
          </span>
        </span>
        {/* The same group tile the Groups screen uses, so a group reads as
         the same object wherever it appears. */}
        <span className="flex items-center gap-2.5" data-id="element-544" data-mp="3hmdizlm120q3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-[#1E3A5F] text-[9.5px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-545" data-mp="3hozx643fbeha">
            OP
          </span>
          <span data-id="element-546" data-mp="98elgfjeq7ah">
            <span className="block text-[10px] font-bold uppercase tracking-[0.09em] text-neutral-400" data-id="element-547" data-mp="97veftvap3b8">
              Shared with
            </span>
            <span className="block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-548" data-mp="3vn6cq44i8rs5">
              Operations · 9 people
            </span>
          </span>
        </span>
      </div>

      <p className="mb-2.5 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-549" data-mp="wz0qdxy47bg9">
        Steps
      </p>
      {/* A rail behind the numbers makes the steps read as one sequence
       rather than four unrelated cards. */}
      <ol className="relative space-y-2.5 before:absolute before:bottom-7 before:left-[30px] before:top-7 before:w-px before:bg-[#EFEAE1] before:content-['']" data-id="element-550" data-mp="14fxeb41nmhni">
        {steps.map((s, i) => <li key={s} className={`relative flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-551" data-mp="1dgvipng08r6m" data-mpi={i}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#2B211A] text-[11px] font-bold tabular-nums text-white shadow-[0_1px_2px_rgba(43,33,26,0.3)]" data-id="element-552" data-mp="1ig82fzbkxi4z">
              {i + 1}
            </span>
            <span className="flex-1 text-[13px] leading-snug text-[#3D3229]" data-id="element-553" data-mp="1s5rnnbmrlo30">
              {s}
            </span>
            {/* The tools each step touches, stated once where they're used
             rather than duplicated in a strip above the list. */}
            <span className="flex shrink-0 items-center gap-1.5" data-id="element-554" data-mp="214v7wixo49j3">
              {(STEP_TOOLS[i] ?? []).map(t => <BrandLogo key={t} slug={t} className="size-[15px] opacity-80" data-id="element-555" data-mp="263g66ctd0j4w" />)}
            </span>
          </li>)}
      </ol>
      {/* Run now is the screen's primary action and was sized like a tag.
       It now matches the buttons used elsewhere in the product screens. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[#EFEAE1] pt-4" data-id="element-556" data-mp="3g1rv45z55c4z">
        <span className="inline-flex items-center gap-2 rounded-xl bg-[#2B211A] px-4 py-2.5 text-[13px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-557" data-mp="3l037ancwryqd">
          <Play size={13} fill="currentColor" data-id="element-558" data-mp="3uy95atvlgkpy" />
          Run now
        </span>
        <span className="text-[12px] tabular-nums text-neutral-500" data-id="element-559" data-mp="3m7q5u2r9wmk">
          Last run Monday 09:00 · 1m 12s
        </span>
      </div>
    </div>;
}
export function PulsesScreen() {
  // A pulse only matters if you know where its output lands, so the
  // destination sits on the row with the mark of the tool it posts to.
  const pulses = [{
    name: 'Monday revenue brief',
    cadence: 'Mondays, 08:00',
    next: 'in 2 days',
    to: '#revenue',
    logo: 'slack',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Ad spend anomaly check',
    cadence: 'Daily, 07:30',
    next: 'in 14 hours',
    to: 'Leadership list',
    logo: 'gmail',
    health: [1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 2]
  }, {
    name: 'Support escalation sweep',
    cadence: 'Every 4 hours',
    next: 'in 51 minutes',
    to: '#support-escalations',
    logo: 'slack',
    // Running right now. State belongs on the thing it describes, not in a
    // banner underneath that made you match a name back to a row.
    running: 'Step 2 of 4 · reading Intercom',
    health: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
  }, {
    name: 'Churn signal digest',
    cadence: 'Fridays, 16:00',
    next: 'in 6 days',
    to: 'Customer success',
    logo: 'microsoftteams',
    health: [1, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1]
  }];
  // One starts paused, so the control reads as a real state rather than a
  // decorative button — and the paused row visibly stops reporting.
  const [paused, setPaused] = React.useState<Record<string, boolean>>({
    'Churn signal digest': true
  });
  const live = pulses.length - Object.values(paused).filter(Boolean).length;
  return <div data-id="element-560" data-mp="3dwindjsgz4wb">
      <ScreenHead title="Pulses" meta={`${live} running · 1 paused · work that happens without being asked`} data-id="element-561" data-mp="3dwa85l8vjnhy" />
      <ul className="space-y-2.5" data-id="element-562" data-mp="2zwrc3qtc82m0">
        {pulses.map(p => {
        const off = !!paused[p.name];
        const running = !off && 'running' in p ? (p as {
          running: string;
        }).running : null;
        return <li key={p.name} className={`flex flex-wrap items-center gap-3.5 px-4 py-3.5 ${ROW} ${running ? 'border-[#EA580C]/25 bg-[#FFF8F3]' : ''}`} data-id="element-563" data-mp="2q04sf5cqzhlk">
              <span className="min-w-0 flex-1" data-id="element-564" data-mp="o7q33l7vjav4">
                <span className={`block truncate text-[13.5px] font-semibold ${off ? 'text-[#8C7F73]' : 'text-[#2B211A]'}`} data-id="element-565" data-mp="o7zvffge5u3z">
                  {p.name}
                </span>
                {/* While a pulse is mid-run, the row reports the run instead of
                 the schedule — the schedule is not the useful fact right now. */}
                {running ? <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] font-medium text-[#C2410C]" data-id="element-566" data-mp="2ne57opjt3qja">
                    <Loader2 size={11} className="shrink-0 animate-spin" data-id="element-567" data-mp="2in6nhevp68ar" />
                    <span className="truncate" data-id="element-568" data-mp="2wf3cvb5ze43g">{running}</span>
                  </span> : <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-569" data-mp="2wda5turghhux">
                    {p.cadence}
                    <span className="text-[#D8CFC2]" data-id="element-570" data-mp="36d9h3zf9fmbh">·</span>
                    <BrandLogo slug={p.logo} className={`size-3.5 ${off ? 'opacity-40' : ''}`} data-id="element-571" data-mp="36chtpbvtguwa" />
                    <span className="truncate" data-id="element-572" data-mp="31eptlul0q2h0">{p.to}</span>
                  </span>}
              </span>
              {/* Twelve ticks say "is this healthy" faster than a percentage. */}
              <span className="flex shrink-0 items-end gap-[3px] rounded-md bg-white px-1.5 py-1 ring-1 ring-[#F1ECE3]" data-id="element-573" data-mp="171gnmcrokz3i">
                {p.health.map((h, i) => <span key={i} className={`h-3.5 w-[3px] rounded-full ${off ? 'bg-[#E2DACE]' : h === 1 ? 'bg-emerald-500/80' : h === 0 ? 'bg-red-500/85' : 'bg-amber-500/85'}`} data-id="element-574" data-mp="1dgf8gjottnlx" data-mpi={i} />)}
              </span>
              <span className="flex w-[92px] shrink-0 items-center justify-end gap-1.5 text-[11.5px] font-medium tabular-nums text-neutral-500" data-id="element-575" data-mp="1nel7mtj90fn3">
                {off ? <span className="text-neutral-400" data-id="element-576" data-mp="ps5olnb6b9xf">Paused</span> : running ? <span className="font-semibold text-[#C2410C]" data-id="element-577" data-mp="upo0nmkhm59n">Running</span> : <>
                    <Clock size={11} className="text-neutral-300" data-id="element-578" data-mp="zp0pudytzofe" />
                    {p.next}
                  </>}
              </span>
              <button type="button" onClick={() => setPaused(s => ({
            ...s,
            [p.name]: !off
          }))} aria-label={off ? `Resume ${p.name}` : `Pause ${p.name}`} className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-[#EFEAE1] bg-white text-[#6B5D52] transition-colors duration-150 ease-out hover:border-[#D8CFC2] hover:text-[#2B211A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]/40" data-id="element-579" data-mp="14gsgsplwfw62">
                {off ? <Play size={12} fill="currentColor" data-id="element-580" data-mp="2g8rhw8wkwgia" /> : <Pause size={12} fill="currentColor" data-id="element-581" data-mp="2g7q3bugctbvt" />}
              </button>
            </li>;
      })}
      </ul>
    </div>;
}

// One sparkle on every row said "AI feature" four times and nothing about the
// skills themselves. A glyph per subject makes the list scannable by what each
// method is for, and the ink tile matches the group tiles on the Groups screen
// so both screens read as the same application.
export function SkillsScreen() {
  const skills = [{
    name: 'How we qualify an inbound lead',
    icon: Filter,
    used: 142,
    group: 'Marketing',
    initials: 'MK',
    color: '#7C2D12',
    updated: '2 days ago'
  }, {
    name: 'Our weekly reporting format',
    icon: BarChart3,
    used: 96,
    group: 'Leadership',
    initials: 'LD',
    color: '#1E3A5F',
    updated: '1 week ago'
  }, {
    name: 'Escalating a P1 incident',
    icon: Siren,
    used: 38,
    group: 'Customer Success',
    initials: 'CS',
    color: '#3F3A2E',
    updated: '3 weeks ago'
  }, {
    name: 'Drafting a renewal email',
    icon: Mail,
    used: 211,
    group: 'Sales',
    initials: 'SL',
    color: '#134E4A',
    updated: 'Yesterday'
  }];
  return <div data-id="element-582" data-mp="1roeplmoa75wd">
      <ScreenHead title="Skills" meta="Recorded once, reused everywhere" data-id="element-583" data-mp="1rnmtmaqrv65k" />
      <ul className="space-y-2.5" data-id="element-584" data-mp="1wn80r8g3iktd">
        {skills.map(s => <li key={s.name} className={`flex items-center gap-3.5 px-4 py-3.5 ${ROW}`} data-id="element-585" data-mp="1wlwpknkbmas8">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#2B211A] shadow-[0_1px_2px_rgba(43,33,26,0.25),0_0_0_1px_rgba(255,255,255,0.12)_inset]" data-id="element-586" data-mp="1iurxc6ja8krx">
              <s.icon size={15} strokeWidth={1.9} className="text-white" data-id="element-587" data-mp="2amqx73zjo7pu" />
            </span>
            <span className="min-w-0 flex-1" data-id="element-588" data-mp="2fe903xl4oidg">
              <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-589" data-mp="2ff0iu7tmmlmb">
                {s.name}
              </span>
              {/* Who it belongs to and whether it is still being tended —
               a stale method is worse than none. */}
              <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-neutral-500" data-id="element-590" data-mp="dni8fbwttncp">
                <span className="flex size-4 shrink-0 items-center justify-center rounded text-[7px] font-bold text-white" style={{
              backgroundColor: s.color
            }} data-id="element-591" data-mp="8p71b8xx1qy6">
                  {s.initials}
                </span>
                <span className="truncate" data-id="element-592" data-mp="3c8a0yy8mc2sr">{s.group}</span>
                <span className="text-[#D8CFC2]" data-id="element-593" data-mp="3r4hj7t0guu5e">·</span>
                <span className="shrink-0 whitespace-nowrap" data-id="element-594" data-mp="3r13jksj6sz1n">
                  Updated {s.updated}
                </span>
              </span>
            </span>
            <span className="shrink-0 rounded-md border border-[#EFEAE1] bg-white px-2 py-1 text-[11px] font-semibold tabular-nums text-neutral-500" data-id="element-595" data-mp="16bntjzrsled2">
              used {s.used}×
            </span>
          </li>)}
      </ul>
      <p className="mt-3 text-[12.5px] leading-relaxed text-neutral-500" data-id="element-596" data-mp="2uus1sp2gup98">
        Every chat, workflow, and pulse in the workspace draws on these, so the
        method stays consistent no matter who asks.
      </p>
    </div>;
}